import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiShoppingCart, FiUpload, FiX } from 'react-icons/fi';
import { useLoadingContext } from '../Loading/LoadingProvider';
import './Customize.css';

// Same sizes as existing products
const SIZES = [
  { name: 'Small', price: 230, dimensions: '20×30 cm' },
  { name: 'Medium', price: 280, dimensions: '30×40 cm' },
  { name: 'Large', price: 400, dimensions: '40×50 cm' },
];

// Same frame colours as existing products
const FRAME_OPTIONS = [
  { name: 'black', label: 'Classic Black', hex: '#1a1a1a' },
  { name: 'white', label: 'Pure White', hex: '#ffffff' },
  { name: 'darkWood', label: 'Natural Wood', hex: '#5D4037' },
];

// Compress an image file to a base64 JPEG (max 800px, 70% quality)
function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const MAX = 800;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) {
            height = Math.round((height * MAX) / width);
            width = MAX;
          } else {
            width = Math.round((width * MAX) / height);
            height = MAX;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function Customize() {
  const { startComponentLoading, stopComponentLoading } = useLoadingContext();

  const [selectedSize, setSelectedSize] = useState(SIZES[0].name);
  const [selectedFrame, setSelectedFrame] = useState('black');
  const [orientation, setOrientation] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);

  const [showOrientation, setShowOrientation] = useState(false);

  const currentPrice = SIZES.find((s) => s.name === selectedSize)?.price ?? 230;

  const handleFileSelect = async (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    try {
      const compressed = await compressImage(file);
      setUploadedImage(compressed);
      setUploadedFileName(file.name);
    } catch (err) {
      console.error('Image compression failed:', err);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) handleFileSelect(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleAddToCart = async () => {
    try {
      setIsAddingToCart(true);
      startComponentLoading('addToCart', 'Curating...');

      const frameLabel = FRAME_OPTIONS.find((f) => f.name === selectedFrame)?.label;
      const sizeData = SIZES.find((s) => s.name === selectedSize);

      const cartItem = {
        id: `custom-${Date.now()}`,
        productId: 'custom',
        name: 'Custom Poster',
        category: 'Customize',
        size: selectedSize,
        dimensions: sizeData?.dimensions,
        color: frameLabel,
        price: currentPrice,
        image: '/cusomize.jpg',
        customImage: uploadedImage,
        orientation: orientation,
        isCustom: true,
        quantity: 1,
      };

      const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
      existingCart.push(cartItem);
      localStorage.setItem('cart', JSON.stringify(existingCart));
      window.dispatchEvent(new CustomEvent('cartUpdated'));

      await new Promise((resolve) => setTimeout(resolve, 800));

      const banner = document.createElement('div');
      banner.className = 'premium-toast';
      banner.innerHTML = `<span>Custom poster added to your selection</span>`;
      document.body.appendChild(banner);
      setTimeout(() => banner.classList.add('visible'), 100);
      setTimeout(() => {
        banner.classList.remove('visible');
        setTimeout(() => banner.remove(), 500);
      }, 3000);
    } catch (error) {
      console.error('Error adding custom poster to cart:', error);
    } finally {
      setIsAddingToCart(false);
      stopComponentLoading('addToCart');
    }
  };

  return (
    <div className="customize-page">
      {/* Back link */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="customize-nav-header"
      >
        <button onClick={() => window.history.back()} className="back-link">
          ← Back
        </button>
      </motion.div>

      <div className="customize-container">
        {/* ── LEFT: Image Panel — always shows the promo image ── */}
        <motion.div
          className="customize-image-section"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="customize-image-wrapper">
            <img
              src="/cusomize.jpg"
              alt="Customize your poster"
              className="customize-main-image"
              style={{ objectFit: 'cover' }}
            />
            <div className="image-overlay-glow" />
          </div>
        </motion.div>

        {/* ── RIGHT: Options Panel ── */}
        <div className="customize-info-section">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <span className="customize-category-tag">Custom Order</span>
            <h1 className="customize-title">Customize Your Poster</h1>

            <div className="price-box">
              <div className="price-display">
                <span className="current-price">{currentPrice} EGP</span>
              </div>
            </div>

            <p className="customize-description">
              Upload your own image, choose the size and frame — we'll print it for you in premium quality.
            </p>

            <div className="customize-options">

              {/* ── Image Upload (FIRST) ── */}
              <div className="option-group">
                <h4>Upload Your Image</h4>

                <AnimatePresence mode="wait">
                  {uploadedImage ? (
                    /* ── Preview state ── */
                    <motion.div
                      key="preview"
                      className="upload-preview"
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.35, ease: 'easeOut' }}
                    >
                      <div className="upload-preview-inner">
                        <img
                          src={uploadedImage}
                          alt="Uploaded preview"
                          className="upload-thumb"
                        />
                        <div className="upload-preview-info">
                          <span className="upload-preview-check">✓</span>
                          <p className="upload-preview-name">{uploadedFileName || 'Image uploaded'}</p>
                          <button
                            className="upload-change-btn"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            Change image
                          </button>
                          <button
                            className="upload-remove-btn"
                            onClick={() => { setUploadedImage(null); setUploadedFileName(''); }}
                            title="Remove"
                          >
                            <FiX /> Remove
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ) : (
                    /* ── Upload zone state ── */
                    <motion.div
                      key="zone"
                      className={`upload-zone${dragOver ? ' drag-over' : ''}`}
                      initial={{ opacity: 0, scale: 0.85 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.85 }}
                      transition={{ duration: 0.3, ease: 'easeOut' }}
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                    >
                      <FiUpload className="upload-icon" />
                      <p className="upload-text">


                        <small>Click or drag your image here</small>
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileInputChange}
                />
              </div>

              {/* ── Size ── */}
              <div className="option-group">
                <h4>Select Dimensions</h4>
                <div className="size-options">
                  {SIZES.map((size) => (
                    <button
                      key={size.name}
                      className={`size-option${selectedSize === size.name ? ' selected' : ''}`}
                      onClick={() => setSelectedSize(size.name)}
                    >
                      {size.dimensions}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Frame ── */}
              <div className="option-group">
                <h4>Choose Gallery Frame</h4>
                <div className="frame-options">
                  {FRAME_OPTIONS.map((frame) => (
                    <button
                      key={frame.name}
                      className={`frame-option${selectedFrame === frame.name ? ' selected' : ''}`}
                      onClick={() => setSelectedFrame(frame.name)}
                    >
                      <span className="color-dot" style={{ background: frame.hex }} />
                      {frame.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Orientation (optional) ── */}
              <div className="option-group">
                <h4>ORIENTATION</h4>
                {!showOrientation ? (
                  <button
                    className="toggle-orientation-btn"
                    onClick={() => setShowOrientation(true)}
                  >
                    + Add Orientation Preference
                  </button>
                ) : (
                  <motion.div
                    className="orientation-options"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{ position: 'relative' }}
                  >
                    <button
                      className={`orientation-btn${orientation === 'vertical' ? ' selected' : ''}`}
                      onClick={() => setOrientation(orientation === 'vertical' ? '' : 'vertical')}
                    >
                      <span className="orient-icon-wrapper">
                        <div className="frame-icon-box vertical"></div>
                      </span>
                      <span>طول <br /><small>Vertical</small></span>
                    </button>
                    <button
                      className={`orientation-btn${orientation === 'horizontal' ? ' selected' : ''}`}
                      onClick={() => setOrientation(orientation === 'horizontal' ? '' : 'horizontal')}
                    >
                      <span className="orient-icon-wrapper">
                        <div className="frame-icon-box horizontal"></div>
                      </span>
                      <span>عرض <br /><small>Horizontal</small></span>
                    </button>
                    <button
                      className="close-orientation-btn"
                      onClick={() => { setShowOrientation(false); setOrientation(''); }}
                      title="Clear & Close"
                    >
                      ✕
                    </button>
                  </motion.div>
                )}
              </div>

            </div>

            {/* Actions */}
            <div className="purchase-actions">
              <button
                className="add-to-cart-premium-btn"
                onClick={handleAddToCart}
                disabled={isAddingToCart}
              >
                {isAddingToCart ? (
                  'CURATING...'
                ) : (
                  <>
                    <FiShoppingCart /> Add to Selection
                  </>
                )}
              </button>
            </div>

            {/* Guarantees */}
            <div className="product-guarantees">
              <div className="guarantee-item">
                <span className="g-icon">✦</span>
                <span className="g-text">Premium Archival Grade</span>
              </div>
              <div className="guarantee-item">
                <span className="g-icon">✦</span>
                <span className="g-text">Hand-Stretched in Egypt</span>
              </div>
            </div>

          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Customize;
